"use client";

import {
	type UseStoreBuilderContextResult,
	useStoreBuilderContext,
} from "@/ui/admin/store-builder/store-builder-context";
import { asyncDebounce, debounce } from "@/ui/hooks/use-debounced-value";
import { Label } from "@/ui/shadcn/label";
import { Switch } from "@/ui/shadcn/switch";
import { StatefulColorPicker } from "@/ui/stateful-color-picker";
import { StatefulInput } from "@/ui/stateful-input";
import { nanoid } from "nanoid";
import { useState } from "react";
import type { YnsComponentForm } from "../types";
import { MenuEditor } from "./menu-editor";
import type { Nav } from "./nav";
import type { MenuItem } from "./schemas";

const debouncedSaveLinks = asyncDebounce(
	(context: UseStoreBuilderContextResult<typeof Nav>, items: MenuItem[]) => {
		return context.saveSelectedPageSectionField({
			fieldPath: "links",
			value: items,
		});
	},
	300,
);

export const NavForm: YnsComponentForm<typeof Nav> = ({ data, theme }) => {
	const context = useStoreBuilderContext<typeof Nav>();

	const [items, setItems] = useState<MenuItem[]>(
		data.links?.map((link) => {
			// legacy links
			if (!("type" in link)) {
				return {
					id: nanoid(),
					type: "custom",
					label: link.label ?? "",
					url: link.href ?? "",
				};
			}
			return link;
		}) ?? [],
	);

	const handleMenuChange = (items: MenuItem[]) => {
		setItems(items);
		debouncedSaveLinks(context, items);
	};

	return (
		<div className="grid gap-4 relative pb-20">
			<Label className="grid gap-2">
				<span>Store Name</span>
				<StatefulInput
					required
					defaultValue={data.title}
					onValueChange={debounce(
						(value) => {
							context.saveSelectedPageSectionField({
								fieldPath: "title",
								value,
							});
						},
						300,
						{ trailing: true },
					)}
				/>
			</Label>

			<div>
				<h5 className="text-sm mb-1 font-semibold">Colors</h5>
				<div className="grid grid-cols-2">
					<Label className="grid gap-1 w-max">
						<span className="text-xs">Background</span>
						<StatefulColorPicker
							defaultValue={theme.backgroundColor}
							onValueChange={debounce(
								(value) => {
									context.saveSelectedPageSectionThemeField({
										fieldPath: "backgroundColor",
										value,
									});
								},
								300,
								{ trailing: true },
							)}
						/>
					</Label>

					<Label className="grid gap-2 w-max">
						<span className="text-xs">Font</span>
						<StatefulColorPicker
							defaultValue={theme.color}
							onValueChange={debounce(
								(value) => {
									context.saveSelectedPageSectionThemeField({
										fieldPath: "color",
										value,
									});
								},
								300,
								{ trailing: true },
							)}
						/>
					</Label>
				</div>
			</div>

			<Label className="flex items-center gap-2 mt-2">
				<Switch
					required
					defaultChecked={data.searchBar?.show ?? true}
					onCheckedChange={debounce(
						(value) => {
							context.saveSelectedPageSectionField({
								fieldPath: "searchBar.show",
								value,
							});
						},
						300,
						{ trailing: true },
					)}
				/>
				<span>Whether to show the search bar</span>
			</Label>

			<MenuEditor items={items} onChange={handleMenuChange} />
		</div>
	);
};
